/**
 * HighlightablePassageText — the interactive passage paragraph.
 *
 * Renders the passage as colour runs (<mark>) containing word spans (<span data-offset>), and
 * owns the pointer gesture that creates and erases highlights.
 *
 * ANNOTATION (2026-07): the same gesture also drives the marking tools, via the optional
 * `annotations` binding. Highlights and marks are different things — a highlight tints words, a
 * mark draws a circle or bracket over them in PassageInkLayer — but the pointer work is
 * identical, so one gesture serves both and only one tool can be armed at a time. Without the
 * binding this component behaves exactly as it did before.
 *
 * LAYOUT INVARIANT: concatenating every rendered text node reproduces the passage exactly.
 * Spans and marks carry no padding, margin, or border, so under `whitespace-pre` the fixed
 * physical lines — and the "line 7" references students depend on — never shift.
 *
 * TOUCH: with a pen armed the paragraph takes `touch-action: pan-y`. Vertical scrolling keeps
 * working; a horizontal drag is a highlight, which matches the direction text runs. The
 * trade-off is that on touch a highlight cannot be dragged across a line break — the browser
 * claims the vertical movement as a pan and fires pointercancel — so students highlight one
 * line at a time. `touch-action` does not apply to mouse, so multi-line mouse drags work.
 *
 * ACCESSIBILITY GAP (accepted, 2026-07-10): highlighting is pointer-only, so it is not
 * reachable by keyboard (WCAG 2.1.1). The decision was that highlighting is a non-essential
 * reading aid — the passage remains fully readable and natively selectable with the pen down.
 * A keyboard path would be: select text natively, then press a "Highlight selection" button
 * that maps the Selection back to offsets via these data-offset spans.
 *
 * The marking tools inherit that gap for the same reason and on the same terms: in Phase 1
 * nothing is graded and nothing is gated on a mark, so a student who cannot use a pointer loses
 * an optional aid, not access to the lesson. The fix is the same one described above — it would
 * serve both tools at once. If a later phase ever makes marks count for anything, this stops
 * being an acceptable trade and has to be built.
 */

import { Fragment, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { PassageInkLayer } from '@/components/PassageInkLayer';
import { markAt, type AnnotationMark, type AnnotationShape } from '@/lib/passageAnnotations';
import {
  HIGHLIGHT_COLORS,
  addHighlight,
  bridgeWhitespaceGaps,
  buildLineRuns,
  offsetInRange,
  snapRange,
  type HighlightColor,
  type HighlightRange,
  type Run,
  type Token,
} from '@/lib/passageHighlights';

/** Pointer travel, in px, past which a gesture is a drag rather than a tap. */
const DRAG_THRESHOLD_PX = 8;

/** Tint shown under the words a marking drag currently covers. The pen colour at 14%. */
const MARK_PREVIEW_TINT = 'rgba(31,90,168,0.14)';

/** Wires the marking tools into the passage gesture. Omit for highlights only. */
export interface PassageAnnotationBinding {
  armedShape: AnnotationShape | null;
  marks: AnnotationMark[];
  /** First end of a link awaiting its second. Shown tinted so the student sees it is held. */
  pendingLink: { start: number; end: number } | null;
  onMarkRange: (anchor: number, focus: number) => void;
  onEraseMarkAt: (offset: number) => void;
}

export interface HighlightablePassageTextProps {
  /** The tokenized passage. Concatenating token.text reproduces the passage exactly. */
  tokens: Token[];
  ranges: HighlightRange[];
  armedColor: HighlightColor | null;
  onHighlightRange: (a: number, b: number) => void;
  onEraseAt: (offset: number) => void;
  /** Applied to the rendered <p>. */
  className?: string;
  /** Optional marking tools. Without it the passage is highlight-only, as before. */
  annotations?: PassageAnnotationBinding;
}

function offsetOf(element: Element | null | undefined): number | null {
  const raw = element?.getAttribute('data-offset');
  if (raw == null) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

/** Resolve the word span under a viewport point. Returns null over whitespace or a gap. */
function wordOffsetAtPoint(x: number, y: number): number | null {
  // jsdom always returns null here, which is why the tap paths below read event.target instead.
  const hit = document.elementFromPoint?.(x, y);
  return offsetOf(hit?.closest('[data-offset]'));
}

export function HighlightablePassageText({
  tokens,
  ranges,
  armedColor,
  onHighlightRange,
  onEraseAt,
  className,
  annotations,
}: HighlightablePassageTextProps) {
  const anchorRef = useRef<number | null>(null);
  const focusRef = useRef<number | null>(null);
  const movedRef = useRef(false);
  const originRef = useRef({ x: 0, y: 0 });
  const pointerIdRef = useRef<number | null>(null);
  const paragraphRef = useRef<HTMLParagraphElement | null>(null);

  const armedShape = annotations?.armedShape ?? null;
  // Exactly one tool is ever armed: the hosts put the highlighter down when a shape is picked
  // and vice versa. `isArmed` is what switches the paragraph into draw mode.
  const isArmed = armedColor !== null || armedShape !== null;

  // Live feedback while dragging. Holds BOTH endpoints rather than reading anchorRef during
  // render — a ref read at render time can serve a stale value. Only set once the gesture is a
  // confirmed drag, so a tap that is about to erase never briefly paints itself the armed colour.
  const [preview, setPreview] = useState<{ anchor: number; focus: number } | null>(null);

  const displayRanges = useMemo(() => {
    if (!preview || !armedColor) return ranges;

    const snapped = snapRange(tokens, preview.anchor, preview.focus);
    if (!snapped) return ranges;
    // Mirrors what usePassageHighlights will commit, so the preview never jumps on release.
    return bridgeWhitespaceGaps(addHighlight(ranges, { ...snapped, color: armedColor }), tokens);
  }, [ranges, tokens, preview, armedColor]);

  /**
   * Words under an in-flight marking drag. A mark has no colour of its own until it is drawn,
   * so the feedback is a plain tint rather than a preview of the shape — which would mean
   * measuring geometry on every pointermove.
   */
  const markPreview = useMemo(() => {
    if (!preview || !armedShape) return null;
    return {
      start: Math.min(preview.anchor, preview.focus),
      end: Math.max(preview.anchor, preview.focus),
    };
  }, [preview, armedShape]);

  /**
   * Words tinted because they are the held first end of a link. Same tint as an in-flight drag,
   * so "this is the bit I have hold of" reads the same either way.
   */
  const tinted = useMemo(
    () => [markPreview, annotations?.pendingLink ?? null].filter((r): r is { start: number; end: number } => r !== null),
    [markPreview, annotations?.pendingLink]
  );

  const lines = useMemo(() => buildLineRuns(tokens, displayRanges), [tokens, displayRanges]);

  // Only used to re-measure the ink layer when the passage itself changes.
  const passageText = useMemo(() => tokens.map((token) => token.text).join(''), [tokens]);

  const reset = () => {
    anchorRef.current = null;
    focusRef.current = null;
    movedRef.current = false;
    pointerIdRef.current = null;
    setPreview(null);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLParagraphElement>) => {
    if (!isArmed) return;

    // Read the anchor from the event target rather than elementFromPoint: it is exact, and it
    // keeps the tap paths testable under jsdom.
    const offset = offsetOf((event.target as Element).closest?.('[data-offset]'));
    if (offset == null) return; // Whitespace, or a line-number prefix (no data-offset).

    event.preventDefault();
    const element = event.currentTarget;
    if (typeof element.setPointerCapture === 'function') {
      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        // Capture is an optimisation; the gesture still works without it.
      }
    }

    anchorRef.current = offset;
    focusRef.current = offset;
    movedRef.current = false;
    originRef.current = { x: event.clientX, y: event.clientY };
    pointerIdRef.current = event.pointerId;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLParagraphElement>) => {
    const anchor = anchorRef.current;
    if (pointerIdRef.current !== event.pointerId || anchor == null) return;

    const dx = event.clientX - originRef.current.x;
    const dy = event.clientY - originRef.current.y;
    if (!movedRef.current && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      movedRef.current = true;
    }

    const offset = wordOffsetAtPoint(event.clientX, event.clientY);
    if (offset != null) focusRef.current = offset;

    if (movedRef.current) {
      event.preventDefault();
      setPreview({ anchor, focus: focusRef.current ?? anchor });
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLParagraphElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;

    const anchor = anchorRef.current;
    const focus = focusRef.current ?? anchor;
    const moved = movedRef.current;
    reset();
    if (anchor == null || focus == null) return;

    if (annotations && armedShape) {
      // Tap on an existing mark lifts it; anything else draws a new one. Same contract as the
      // highlighter's tap-to-erase, so the two tools behave identically under the finger.
      if (!moved && markAt(annotations.marks, anchor)) {
        annotations.onEraseMarkAt(anchor);
      } else {
        annotations.onMarkRange(anchor, focus);
      }
      return;
    }

    if (!moved && offsetInRange(ranges, anchor)) {
      onEraseAt(anchor);
    } else {
      onHighlightRange(anchor, focus);
    }
  };

  const armedStyle: React.CSSProperties = {
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    // Suppresses iOS Safari's selection magnifier and Copy/Look Up callout.
    WebkitTouchCallout: 'none',
    cursor: 'crosshair',
  };

  return (
    <p
      ref={paragraphRef}
      // `relative` positions the ink layer. It adds no box of its own, so the passage's
      // `whitespace-pre` line geometry — and every "line 7" reference — is unaffected.
      className={cn('relative', className)}
      style={isArmed ? armedStyle : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={reset}
    >
      {lines.map((runs, lineIndex) => (
        <Fragment key={lineIndex}>
          {/* Newlines are implied by the line array; re-emit them so `whitespace-pre` sees the
              exact original text. Never write literal JSX whitespace between these nodes. */}
          {lineIndex > 0 ? '\n' : null}
          {runs.map((run) => (
            <RunNodes key={run.tokens[0].start} run={run} tinted={tinted} />
          ))}
        </Fragment>
      ))}
      {annotations ? (
        <PassageInkLayer
          marks={annotations.marks}
          containerRef={paragraphRef}
          passage={passageText}
        />
      ) : null}
    </p>
  );
}

function RunNodes({ run, tinted }: { run: Run; tinted: { start: number; end: number }[] }) {
  const children = run.tokens.map((token) =>
    token.type === 'word' && token.highlightable ? (
      <span
        key={token.start}
        data-offset={token.start}
        // Background only — never padding or a border, which would move the line.
        style={
          tinted.some((r) => token.start >= r.start && token.start <= r.end)
            ? { backgroundColor: MARK_PREVIEW_TINT }
            : undefined
        }
      >
        {token.text}
      </span>
    ) : (
      token.text
    )
  );

  if (!run.color) return <>{children}</>;

  return (
    <mark
      style={{
        backgroundColor: HIGHLIGHT_COLORS[run.color],
        // Override the <mark> UA default (black on yellow) and keep line metrics untouched.
        color: 'inherit',
        padding: 0,
        borderRadius: '2px',
      }}
    >
      {children}
    </mark>
  );
}
